import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Icon tile (DESIGN_SYSTEM §4, T-093): a tinted square holding one lucide icon. Decorative —
 * the row or card next to it carries the meaning in text, so the tile is hidden from AT.
 * Tones follow the work-item kinds: primary (approve), danger (fix), accent (complete),
 * success (done), warning, neutral.
 */
const iconTileVariants = cva('inline-flex shrink-0 items-center justify-center', {
  variants: {
    tone: {
      neutral: 'bg-surface-muted text-muted-foreground',
      primary: 'bg-primary-soft text-primary',
      success: 'bg-success-soft text-success',
      warning: 'bg-warning-soft text-warning',
      danger: 'bg-danger-soft text-danger',
      accent: 'bg-accent-soft text-accent-ink',
    },
    size: {
      sm: 'size-8 rounded-md [&_svg]:size-4',
      md: 'size-10 rounded-md [&_svg]:size-5',
    },
  },
  defaultVariants: { tone: 'primary', size: 'md' },
});

type IconTileTone = NonNullable<VariantProps<typeof iconTileVariants>['tone']>;

function IconTile({
  icon: Icon,
  tone,
  size,
  className,
  ...props
}: Omit<React.ComponentProps<'span'>, 'children'> &
  VariantProps<typeof iconTileVariants> & { icon: LucideIcon }) {
  return (
    <span
      data-slot="icon-tile"
      data-tone={tone ?? 'primary'}
      aria-hidden="true"
      className={cn(iconTileVariants({ tone, size }), className)}
      {...props}
    >
      <Icon />
    </span>
  );
}

export { IconTile, iconTileVariants, type IconTileTone };
